/**
 * Korvéy Page Lock — lock page password form.
 *
 * Handles the "Already have access?" form on the Lock Page section:
 * - Submits on button click or Enter key (native form submit covers both).
 * - Compares a SHA-256 hash of the entered password against the hash
 *   rendered by Liquid (settings.page_lock_password | sha256), so the
 *   real password is never present in the page source.
 * - Comparison is case-insensitive: the entered value is lowercased
 *   before hashing, matching the lowercased hash rendered by Liquid,
 *   so "Password", "password", and "PASSWORD" all grant access.
 * - On success: writes sessionStorage and redirects to the protected page.
 * - On failure: shows an accessible inline error, no page reload.
 */
(function () {
  'use strict';

  var settings = window.KorveyLockPageSettings;
  if (!settings) return;

  var form = document.querySelector('[data-korvey-lock-form]');
  if (!form) return;

  // Guard against this script running twice on the same page
  // (e.g. Theme Editor re-renders a section).
  if (form.dataset.korveyBound === 'true') return;
  form.dataset.korveyBound = 'true';

  var passwordInput = form.querySelector('[data-korvey-password-input]');
  var submitButton = form.querySelector('[data-korvey-submit]');
  var errorEl = document.getElementById('korvey-lock-error');
  var toggleBtn = form.querySelector('[data-korvey-toggle-password]');
  var eyeOpen = toggleBtn ? toggleBtn.querySelector('[data-korvey-eye-open]') : null;
  var eyeClosed = toggleBtn ? toggleBtn.querySelector('[data-korvey-eye-closed]') : null;

  var STORAGE_KEY = 'korvey_page_access';
  var GRANTED_VALUE = 'granted';

  /** SHA-256 hash of a string, returned as lowercase hex. */
  function sha256Hex(message) {
    var data = new TextEncoder().encode(message);
    return window.crypto.subtle.digest('SHA-256', data).then(function (buffer) {
      var bytes = new Uint8Array(buffer);
      var hex = '';
      for (var i = 0; i < bytes.length; i++) {
        hex += bytes[i].toString(16).padStart(2, '0');
      }
      return hex;
    });
  }

  function showError() {
    if (!errorEl) return;
    errorEl.hidden = false;
    passwordInput.setAttribute('aria-invalid', 'true');
    form.classList.add('has-error');
  }

  function clearError() {
    if (!errorEl || errorEl.hidden) return;
    errorEl.hidden = true;
    passwordInput.removeAttribute('aria-invalid');
    form.classList.remove('has-error');
  }

  function setLoading(isLoading) {
    if (!submitButton) return;
    submitButton.disabled = isLoading;
    submitButton.classList.toggle('is-loading', isLoading);
  }

  function grantAccessAndRedirect() {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, GRANTED_VALUE);
    } catch (err) {
      // sessionStorage unavailable — still proceed with the redirect,
      // the protected-page guard will simply re-check on load.
    }
    if (settings.redirectUrl) {
      window.location.href = settings.redirectUrl;
    }
  }

  // Clear the error the moment the visitor starts correcting their input.
  passwordInput.addEventListener('input', clearError);

  if (toggleBtn) {
    toggleBtn.addEventListener('click', function () {
      var isCurrentlyPassword = passwordInput.type === 'password';
      passwordInput.type = isCurrentlyPassword ? 'text' : 'password';
      toggleBtn.setAttribute('aria-label', isCurrentlyPassword ? 'Hide password' : 'Show password');
      if (eyeOpen) eyeOpen.hidden = isCurrentlyPassword;
      if (eyeClosed) eyeClosed.hidden = !isCurrentlyPassword;
      passwordInput.focus();
    });
  }

  form.addEventListener('submit', function (evt) {
    evt.preventDefault();

    if (!settings.enabled) return;

    var entered = passwordInput.value; // used exactly as typed

    if (!entered) {
      showError();
      return;
    }

    setLoading(true);

    // Lowercase before hashing so the match is case-insensitive.
    sha256Hex(entered.toLowerCase())
      .then(function (enteredHash) {
        setLoading(false);
        if (enteredHash === settings.passwordHash) {
          clearError();
          grantAccessAndRedirect();
        } else {
          showError();
        }
      })
      .catch(function () {
        setLoading(false);
        showError();
      });
  });
})();

/**
 * Helper kept for future use — e.g. wiring up a logout link/button
 * on the protected page later. Not called anywhere by default.
 */
function korveyRemovePageAccess() {
  try {
    window.sessionStorage.removeItem('korvey_page_access');
  } catch (err) {
    /* no-op */
  }
}